import React, { useState, useCallback, useEffect } from "react";
import apiClient from "../../auth/apiClient";
import { useSelector } from "react-redux";
import ImportService from "../../services/ImportService";
import { DropzoneArea } from "material-ui-dropzone";
import {
  Button,
  Typography,
  Grid,
  Box,
  Link,
  Card,
  CardContent,
  LinearProgress,
  Paper,
} from "@material-ui/core";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import { useStyles } from "./styles";
import { isUndefined } from "lodash";
import ImportHistoryTable from "./ImportHistory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import {useCoreEntityContext} from "../../hooks/useCoreEntitySlice";
import useHubContext from "../../hooks/useHubContext";

const importService = new ImportService(apiClient);

const TERMINAL_STATUSES = ["completedsuccess", "completederror", "canceled"];

const isInProgressStatus = status =>
  typeof status === "string" && status.toLowerCase() === "inprogress";

const isTerminalStatus = status =>
  typeof status === "string" &&
  TERMINAL_STATUSES.includes(status.toLowerCase());

// The progress payload pushed over the hub may arrive as a raw object, a JSON
// string, or an envelope whose `.Message` is itself a JSON string. Normalize all
// of those into a single plain object before reading progress fields.
const normalizeProgressPayload = data => {
  if (data === null || isUndefined(data)) return undefined;

  let payload = data;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return undefined;
    }
  }

  // hub-host wraps the progress in a StreamPayload and relays it; its inner `message`
  // (camelCase from hub-host, or `Message` PascalCase) is itself the JSON progress string.
  const nestedMessage = payload?.message ?? payload?.Message;
  if (typeof nestedMessage === "string") {
    try {
      payload = JSON.parse(nestedMessage);
    } catch {
      // Keep the already-parsed envelope if the nested message is not JSON.
    }
  }

  return payload;
};

// Read progress numbers tolerating both snake_case (hub payload) and PascalCase.
const readProgressFields = payload => {
  if (!payload || typeof payload !== "object") return undefined;

  const countCompleted =
    payload.count_completed ?? payload.CountCompleted ?? 0;
  const countTotal = payload.count_total ?? payload.CountTotal ?? 0;
  let percent = payload.percent ?? payload.Percent;
  if (isUndefined(percent) || percent === null) {
    percent = countTotal
      ? Math.round((100 * countCompleted) / countTotal)
      : 0;
  }
  percent = Math.max(0, Math.min(100, percent));

  return { countCompleted, countTotal, percent };
};

const Import = () => {
  const classes = useStyles();
  const enqueueSnackbar = useEnqueueSnackbar();
  const useCoreEntitySlice = useCoreEntityContext();
  const { portalHub, Connected: PortalHubConnected } = useHubContext() ?? {};

  const currentUserId = useSelector(state => state.user?.UserID);
  const facilityId = useSelector(state =>useCoreEntitySlice ? state.coreEntities?.ContextID : state.entities?.ContextID);
  const scopeAwareFacilityID = useSelector((state) => state.entityScope?.facilityGroupId || facilityId);
  const [historyData, setHistoryData] = useState([]);
  const [showHistoryError, setShowHistoryError] = useState(false);
  const [isProcessing, setIsProcressing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [showBtn, setShowBtn] = useState(false);
  const [file, setFile] = useState();
  const [displayResponse, setDisplayResponse] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  const [isImportInProgress, setIsImportInProgress] = useState(false);
  const [progress, setProgress] = useState(undefined);
  const [activeImportId, setActiveImportId] = useState(undefined);
  const [blockedInfo, setBlockedInfo] = useState(undefined);

  const fetchHistory = useCallback(async () => {
    try {
      let response = await importService.getImportsByFacilityId(scopeAwareFacilityID);
      return response.data;
    } catch {
      return undefined;
    }
  });

  const handleFetchHistory = () => {
    setIsFetching(true);
    fetchHistory().then(history => {
      if (isUndefined(history)) {
        setShowHistoryError(true);
      } else {
        setHistoryData(history);
        setShowHistoryError(false);
        // Keep the active import id in sync with the InProgress row so the Cancel
        // control always has an id to send, even when the live feed has not yet
        // delivered a payload (the upload response does not carry an import id).
        const inProgressRow = history.find(row => isInProgressStatus(row.status));
        if (inProgressRow) {
          setActiveImportId(inProgressRow.importID);
        }
      }
    });
    setIsFetching(false);
  };

  // 7.6 - On mount, re-read persisted status/progress. If an import is still
  // InProgress for this facility scope, surface its progress and lock the page
  // into the in-progress state (disable upload, show Cancel).
  useEffect(() => {
    let cancelled = false;
    fetchHistory().then(history => {
      if (cancelled || isUndefined(history)) return;
      const inProgressRow = history.find(row => isInProgressStatus(row.status));
      if (inProgressRow) {
        setIsImportInProgress(true);
        setActiveImportId(inProgressRow.importID);
        setProgress(readProgressFields(inProgressRow));
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeAwareFacilityID]);

  // 7.1 - Subscribe to the live progress topic for this facility scope and
  // update the progress bar / counts from the pushed payload.
  useEffect(() => {
    if (!PortalHubConnected || !portalHub?.isOpen || !facilityId) {
      return;
    }

    const topic = `${facilityId}_ACCESS_HOLDER_IMPORT_PROGRESS`;

    portalHub.subscribe(topic, data => {
      const payload = normalizeProgressPayload(data);
      const fields = readProgressFields(payload);
      if (!fields) return;

      const status = payload?.status ?? payload?.Status;
      const importId =
        payload?.importId ??
        payload?.import_id ??
        payload?.importID ??
        payload?.ImportId;
      if (!isUndefined(importId) && importId !== null) {
        setActiveImportId(importId);
      }

      if (isTerminalStatus(status)) {
        // 7.2 - On any terminal status (including a failed import where no chunk
        // ever committed), tear down the in-progress UI: hide the bar, clear
        // progress, and re-enable upload. Then refresh history so the resulting
        // row (e.g. Completed Error with its Failed Imports download) is shown.
        setIsImportInProgress(false);
        setProgress(undefined);
        setActiveImportId(undefined);
        handleFetchHistory();
      } else {
        setProgress(fields);
        setIsImportInProgress(true);
      }
    });

    return () => {
      portalHub.unsubscribe(topic);
    };
  }, [PortalHubConnected, portalHub, facilityId]);

  const handleAddFile = e => {
    if (e.length === 0) return;
    setShowBtn(true);
    setFile(e[0]);
    setErrorMessages([]);
  };
  const handleDeleteFile = () => {
    setFile(undefined);
    setShowBtn(false);
    setErrorMessages([]);
  };

  const handleSubmit = async () => {
    setIsProcressing(true);
    setBlockedInfo(undefined);

    try {
      var response = await importService.uploadFile(
        currentUserId,
        file,
        scopeAwareFacilityID,
        facilityId
      );
      if (response.status === 200) {
        enqueueSnackbar("Successfully uploaded file to server", {
          variant: "success",
          tag: "uploadFileSuccess",
        });
        // 7.2 - an accepted upload means an import is now in progress.
        setIsImportInProgress(true);
        setProgress(undefined);
        // The upload response (ImportCheckCommandVM) does not carry an import id.
        // Refresh history so the new row is visible and the active import id is
        // resolved from the InProgress row; the live feed then drives the bar.
        handleFetchHistory();
      }
    } catch (err) {
        setErrorMessages(err.response.data.errorMessages);
    }

    setIsProcressing(false);
    setDisplayResponse(true);
  };

  // 7.3 - Cancel the in-progress import. No success toast; the UI returning to
  // its idle state is the confirmation. Toast only on failure.
  const handleCancelImport = async importId => {
    // The id may not be set yet (the upload response carries none and the live
    // feed may not have pushed a payload). Fall back to resolving the InProgress
    // import from history so the cancel API still fires.
    let targetImportId = importId;
    if (isUndefined(targetImportId) || targetImportId === null) {
      const history = await fetchHistory();
      const inProgressRow = Array.isArray(history)
        ? history.find(row => isInProgressStatus(row.status))
        : undefined;
      targetImportId = inProgressRow?.importID;
    }

    if (isUndefined(targetImportId) || targetImportId === null) {
      enqueueSnackbar("No in-progress import to cancel.", {
        variant: "error",
        tag: "cancelImportError",
      });
      return;
    }

    try {
      await importService.cancelImport(targetImportId);
      setIsImportInProgress(false);
      setProgress(undefined);
      setActiveImportId(undefined);
      setBlockedInfo(undefined);
    } catch {
      enqueueSnackbar("Failed to cancel import. Please try again.", {
        variant: "error",
        tag: "cancelImportError",
      });
    }
  };

  // 7.5 - Download the failed-imports CSV for an errored history row. No success
  // toast; the browser download is the confirmation. Toast only on failure.
  const handleDownloadFailedImports = async importId => {
    if (isUndefined(importId) || importId === null) return;
    try {
      const response = await importService.getFailedImportsDownload(importId);
      const url = response?.data?.url;
      const message =
        response?.data?.message ?? "This file is only available for 7 days.";
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.click();
      }
      enqueueSnackbar(message, {
        variant: "info",
        tag: "failedImportsMessage",
      });
    } catch {
      enqueueSnackbar("Failed to retrieve failed imports file. Please try again.", {
        variant: "error",
        tag: "failedImportsError",
      });
    }
  };

  return (
    <Grid container>
      <Grid item>
        <Typography className={classes.title} variant="h5" color="primary">
          Access Holder Import
        </Typography>
      </Grid>
      <Grid item className={classes.title}>
        <Typography>
          This is a one time import that allows you to add access holders to the
          system in bulk. Please use this{" "}
          <Link
            underline="always"
            href={process.env.PUBLIC_URL + "/files/Amano_ONE_sample_import.csv"}
          >
            Sample CSV
          </Link>{" "}
          as an example of how to format your import file.
        </Typography>
      </Grid>
      <Grid item className={classes.dropboxContainer}>
        <DropzoneArea
          className={classes.previewChip}
          acceptedFiles={[
            ".csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values",
          ]}
          data-testid="dropzone"
          filesLimit={1}
          type="file"
          onChange={handleAddFile}
          onDelete={handleDeleteFile}
          dropzoneProps={{ disabled: isImportInProgress }}
        />
        {showBtn && (
          <Button
            className={classes.submitBtn}
            variant="contained"
            color="primary"
            id="submitFileBtn"
            disabled={isProcessing || isImportInProgress}
            onClick={handleSubmit}
          >
            {isProcessing ? (
              <FontAwesomeIcon
                className={classes.icon}
                icon={faSpinner}
                pulse
                size="lg"
              />
            ) : (
              "Submit"
            )}
          </Button>
        )}
        {displayResponse && (
          <Grid item>
            {errorMessages && (
              <Card className={classes.errorBox} id="errorBox">
                {errorMessages.map((message, index) => (
                  <CardContent className={classes.error} key={index}>
                    {message}
                  </CardContent>
                ))}
              </Card>
            )}
          </Grid>
        )}
      </Grid>

      {/* 7.1 - Live progress bar and counts while an import is in progress. */}
      {isImportInProgress && (
        <Grid item xs={12}>
          <Paper
            className={classes.progressContainer}
            variant="outlined"
            id="importProgress"
          >
            <Typography variant="subtitle2" color="primary">
              Import in progress
            </Typography>
            <LinearProgress
              className={classes.progressBar}
              variant="determinate"
              value={progress?.percent ?? 0}
            />
            <Box className={classes.progressCounts}>
              <Typography variant="body2">
                Completed: {progress?.countCompleted ?? 0} / {progress?.countTotal ?? 0}
              </Typography>
              <Typography variant="body2">
                {progress?.percent ?? 0}%
              </Typography>
            </Box>
            {/* 7.3 - Cancel control available to any viewer while in progress. */}
            <Button
              className={classes.cancelBtn}
              variant="outlined"
              color="secondary"
              id="cancelImportBtn"
              onClick={() => handleCancelImport(activeImportId)}
            >
              Cancel Import
            </Button>
          </Paper>
        </Grid>
      )}

      {/* 7.4 - Blocked because another user already has an import running. */}
      {blockedInfo && (
        <Grid item xs={12}>
          <Paper className={classes.blockedBox} variant="outlined" id="blockedBox">
            <Typography variant="subtitle1" className={classes.blockedTitle}>
              An import is already running by another user
            </Typography>
            <LinearProgress
              className={classes.progressBar}
              variant="determinate"
              value={blockedInfo.percent}
            />
            <Box className={classes.progressCounts}>
              <Typography variant="body2">
                Completed: {blockedInfo.countCompleted} / {blockedInfo.countTotal}
              </Typography>
              <Typography variant="body2">{blockedInfo.percent}%</Typography>
            </Box>
            <Button
              className={classes.cancelBtn}
              variant="outlined"
              color="secondary"
              id="blockedCancelImportBtn"
              onClick={() => handleCancelImport(blockedInfo.importId)}
            >
              Cancel Import
            </Button>
          </Paper>
        </Grid>
      )}

      <Grid item className={classes.gridRow}>
        {historyData.length !== 0 && (
          <Typography className={classes.title} variant="h6" color="primary">
            History
          </Typography>
        )}
        <Button
          className={classes.getHistoryBtn}
          variant="outlined"
          color="primary"
          onClick={handleFetchHistory}
          disabled={isFetching}
        >
          Get History
        </Button>
      </Grid>
      {showHistoryError && (
        <Grid item className={classes.errorBox}>
          <Card>
            <CardContent className={classes.error}>
              No import history found.
            </CardContent>
          </Card>
        </Grid>
      )}
      {historyData.length !== 0 && (
        <Grid item className={classes.tableContainer}>
          <ImportHistoryTable
            rows={historyData}
            onDownloadFailed={handleDownloadFailedImports}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default Import;
